import * as THREE from 'three';
import { FontLoader, type Font } from 'three/addons/loaders/FontLoader.js';
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import type { PergolaConfig } from '../config/types';

const LINE_COLOR = 0x20242b; // negro suave
const OFFSET = 0.6; // distancia de la línea de cota al borde de la pérgola (m)
const ARROW_LEN = 0.1;
const ARROW_RAD = 0.026;

/**
 * Cotas técnicas dinámicas (ancho, largo, altura): líneas finas negras con flechas
 * y líneas de extensión + etiquetas HTML (CSS2D, tipografía pequeña tipo plano).
 * Los nombres de los lados van como texto 3D tumbado en el suelo.
 */
export class Dimensions {
  readonly group = new THREE.Group();

  private font: Font | null = null;
  private cssLabels: CSS2DObject[] = [];
  private lastConfig: PergolaConfig | null = null;
  private _visible = true;

  private readonly lineMat = new THREE.LineBasicMaterial({ color: LINE_COLOR, transparent: true, opacity: 0.85 });
  private readonly arrowMat = new THREE.MeshBasicMaterial({ color: LINE_COLOR });
  private readonly floorTextMat = new THREE.MeshBasicMaterial({ color: 0x394049 });
  private readonly floorHintMat = new THREE.MeshBasicMaterial({ color: 0x6b7280 });

  constructor() {
    this.group.name = 'dimensions';
  }

  async loadFont(url: string): Promise<void> {
    this.font = await new FontLoader().loadAsync(url);
    if (this.lastConfig) this.update(this.lastConfig); // redibuja los rótulos del suelo
  }

  /** Reconstruye las cotas a partir de las dimensiones de la configuración. */
  update(config: PergolaConfig): void {
    this.lastConfig = config;
    this.clear();

    const W = config.widthMm / 1000;
    const L = config.lengthMm / 1000;
    const H = config.heightMm / 1000;
    const hx = W / 2;
    const hz = L / 2;

    // --- Ancho (eje X), delante de la pérgola ---
    const zFront = hz + OFFSET;
    this.addLinear(new THREE.Vector3(-hx, 0.02, zFront), new THREE.Vector3(hx, 0.02, zFront), `${config.widthMm.toFixed(2)} mm`);
    this.addExtension(new THREE.Vector3(-hx, 0.02, hz), new THREE.Vector3(-hx, 0.02, zFront));
    this.addExtension(new THREE.Vector3(hx, 0.02, hz), new THREE.Vector3(hx, 0.02, zFront));

    // --- Largo (eje Z), lateral derecho ---
    const xSide = hx + OFFSET;
    this.addLinear(new THREE.Vector3(xSide, 0.02, -hz), new THREE.Vector3(xSide, 0.02, hz), `${config.lengthMm.toFixed(2)} mm`);
    this.addExtension(new THREE.Vector3(hx, 0.02, -hz), new THREE.Vector3(xSide, 0.02, -hz));
    this.addExtension(new THREE.Vector3(hx, 0.02, hz), new THREE.Vector3(xSide, 0.02, hz));

    // --- Altura (eje Y), esquina frontal izquierda ---
    const xH = -hx - OFFSET;
    this.addLinear(new THREE.Vector3(xH, 0, hz), new THREE.Vector3(xH, H, hz), `${config.heightMm.toFixed(2)} mm`);
    this.addExtension(new THREE.Vector3(-hx, 0.02, hz), new THREE.Vector3(xH, 0.02, hz));
    this.addExtension(new THREE.Vector3(-hx, H, hz), new THREE.Vector3(xH, H, hz));

    // --- Rótulos en el suelo: nombres de lados + ancho/largo ---
    const inset = 0.4;
    const back = config.tipo === 'adosada' ? 'MURO' : 'TRASERO';
    this.addFloorText(back, 0.1, new THREE.Vector3(0, 0.02, -hz + inset), 0, this.floorTextMat);
    this.addFloorText('FRONTAL', 0.1, new THREE.Vector3(0, 0.02, hz - inset), 0, this.floorTextMat);
    this.addFloorText('IZQUIERDA', 0.1, new THREE.Vector3(-hx + inset, 0.02, 0), Math.PI / 2, this.floorTextMat);
    this.addFloorText('DERECHA', 0.1, new THREE.Vector3(hx - inset, 0.02, 0), -Math.PI / 2, this.floorTextMat);
    this.addFloorText('ancho', 0.075, new THREE.Vector3(0, 0.02, zFront - 0.16), 0, this.floorHintMat);
    this.addFloorText('largo', 0.075, new THREE.Vector3(xSide - 0.16, 0.02, 0), Math.PI / 2, this.floorHintMat);

    this.applyVisibility();
  }

  setVisible(v: boolean): void {
    this._visible = v;
    this.applyVisibility();
  }
  get visible(): boolean {
    return this._visible;
  }
  toggle(): boolean {
    this.setVisible(!this._visible);
    return this._visible;
  }

  // ---------- construcción ----------
  private addLinear(a: THREE.Vector3, b: THREE.Vector3, text: string): void {
    this.group.add(this.line([a, b]));
    const dir = b.clone().sub(a).normalize();
    this.group.add(this.arrow(a, dir.clone().negate()));
    this.group.add(this.arrow(b, dir));
    this.addLabel(text, a.clone().add(b).multiplyScalar(0.5));
  }

  private addExtension(a: THREE.Vector3, b: THREE.Vector3): void {
    this.group.add(this.line([a, b]));
  }

  private line(points: THREE.Vector3[]): THREE.Line {
    return new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), this.lineMat);
  }

  private arrow(pos: THREE.Vector3, dir: THREE.Vector3): THREE.Mesh {
    const cone = new THREE.Mesh(new THREE.ConeGeometry(ARROW_RAD, ARROW_LEN, 10), this.arrowMat);
    cone.position.copy(pos);
    cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    return cone;
  }

  /** Etiqueta HTML (CSS2D) con tipografía pequeña, siempre de cara a la cámara. */
  private addLabel(text: string, position: THREE.Vector3): void {
    const div = document.createElement('div');
    div.className = 'cota-label';
    div.textContent = text;
    const obj = new CSS2DObject(div);
    obj.position.copy(position);
    obj.center.set(0.5, 0.5);
    this.group.add(obj);
    this.cssLabels.push(obj);
  }

  /** Texto plano tumbado sobre el suelo (no billboard), rotado en el plano por `angle`. */
  private addFloorText(
    text: string,
    size: number,
    position: THREE.Vector3,
    angle: number,
    mat: THREE.MeshBasicMaterial,
  ): void {
    if (!this.font) return;
    const geo = new TextGeometry(text, { font: this.font, size, depth: 0.002, curveSegments: 4 });
    geo.center();
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotateX(-Math.PI / 2);
    mesh.rotateZ(angle);
    mesh.position.copy(position);
    this.group.add(mesh);
  }

  private applyVisibility(): void {
    this.group.visible = this._visible;
    for (const l of this.cssLabels) l.visible = this._visible; // CSS2D no hereda del grupo
  }

  private clear(): void {
    for (let i = this.group.children.length - 1; i >= 0; i--) {
      const child = this.group.children[i];
      this.group.remove(child);
      const css = child as unknown as { isCSS2DObject?: boolean; element?: HTMLElement };
      if (css.isCSS2DObject && css.element) {
        css.element.remove();
      } else {
        child.traverse((node) => (node as THREE.Mesh | THREE.Line).geometry?.dispose());
      }
    }
    this.cssLabels = [];
  }

  dispose(): void {
    this.clear();
    this.lineMat.dispose();
    this.arrowMat.dispose();
    this.floorTextMat.dispose();
    this.floorHintMat.dispose();
  }
}
